import React, { FC, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import { Button, LinkButton } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionSM, EyebrowLG, HeadingXS } from '@/atoms/Text'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { MinimalistFooter } from '@/organisms/MinimalistFooter'
import { Offer } from '@/services/PifService'
import { buildLoginUrl } from '@/utils/BuildLoginUrl'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { ExpressReceipt } from '@/views/ExpressCheckoutView/ExpressCheckoutComplete/components/ExpressReceipt'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { CheckoutLegalTerms } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/CheckoutLegalTerms'
import ExpressProcessingOverlay from '../components/ExpressProcessingOverlay'
import { getBonusAccessConfig, getPerksDisclaimerText, useExpressCheckoutConfirmationOnboarding } from '../utils'

interface Props {
  offer: Offer
  projectSlug: string
  config: NonNullable<ReturnType<typeof getBonusAccessConfig>>
}

export const ExpressCheckoutCompleteBonusAccess: FC<Props> = ({ offer, projectSlug, config }) => {
  const { t } = useTranslate('pif')
  const { isLoggedIn, isGuest, email, loading } = useExpressCheckoutConfirmationOnboarding()
  const router = useSafeRouter()
  const track = useSafeTrack()

  useEffect(() => {
    if (!loading) {
      document.body.scrollTo(0, 0)
    }
  }, [loading])

  const handleLoginClick = useCallback(() => {
    track('PIF Checkout CTA Clicked', {
      action: 'Go to login',
      offer,
      projectSlug,
      isLoggedIn,
      isGuest,
    })

    router.push(`${buildLoginUrl(config.watchPath)}&email=${encodeURIComponent(email ?? '')}`)
  }, [email, offer, projectSlug, router, track, isLoggedIn, isGuest, config.watchPath])

  const perksDisclaimerText = getPerksDisclaimerText(projectSlug, t)

  if (loading) {
    return <ExpressProcessingOverlay />
  }

  return (
    <ThemeContextProvider isDarkMode={false}>
      <div className={classNames('bg-core-gray-100')}>
        <FlexColumn className="relative mx-auto max-w-[640px] gap-8 px-6 py-8">
          <div>
            <Image
              alt={'logo'}
              src={getCloudinaryImageUrl(config.logo)}
              width={config.logo.width}
              height={config.logo.height}
              className="mx-auto mb-4"
            />
            <FlexColumn className="gap-2 text-center">
              <EyebrowLG color="core-gray-600" className="uppercase" weight="normal">
                {t('orderCompleted', 'Order Completed')}
              </EyebrowLG>
              <HeadingXS weight="bold" color="core-gray-950" className="text-balance">
                {t('claimAccessToBonusContent', 'Claim access to {{title}}', { title: config.title })}
              </HeadingXS>
            </FlexColumn>
          </div>

          <div className="-mb-6 pt-3 text-center">
            <hr className="border-core-gray-400" />
            <div className={classNames('px-2 inline-block -translate-y-3 bg-core-gray-100')}>
              <PlaySquareIcon color="gray-400" />
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <HeadingXS weight="bold" className="flex items-center gap-2">
              <PlaySquareIcon color="black" />
              {t('howToWatch', 'How to watch')}
            </HeadingXS>

            <div className="flex gap-2">
              <ArrowRightIcon color="homestead-yellow" className="shrink-0" />
              <div>
                <CaptionLG className="mb-4">
                  <Translate
                    t={t}
                    i18nKey="accessToBonusContentGrantedToEmail3"
                    defaults="Access to <bold>{{title}}</bold> has been granted to:"
                    components={{ bold: <b /> }}
                    values={{
                      title: config.title,
                    }}
                  />
                </CaptionLG>
                <CaptionLG
                  weight="bold"
                  className="inline-block rounded-lg border border-dashed border-core-warning-300 bg-core-warning-50 px-3 py-2"
                >
                  {email}
                </CaptionLG>
              </div>
            </div>

            {isGuest && !isLoggedIn && (
              <div className="flex flex-col gap-4">
                <div className="flex gap-2">
                  <ArrowRightIcon color="homestead-yellow" className="shrink-0" />
                  <CaptionLG>
                    <Translate t={t} i18nKey="instructionsOnSettingAPasswordHaveBeenSentToWatch">
                      <b>Instructions on setting a password have been sent to the email above.</b> After setting your
                      password, you can log in to watch.
                    </Translate>
                  </CaptionLG>
                </div>
              </div>
            )}

            {!isLoggedIn && (
              <div className="flex flex-col gap-4">
                <div className="flex gap-2">
                  <ArrowRightIcon color="homestead-yellow" className="shrink-0" />
                  <CaptionLG>
                    {email && (
                      <Translate t={t} i18nKey="logInToYourAngelAccountUsingAboveEmail">
                        <b>Log in to your Angel account</b> using the above email address.
                      </Translate>
                    )}
                    {!email && (
                      <Translate t={t} i18nKey="logInToYourAngelAccount">
                        <b>Log in to your Angel account</b>.
                      </Translate>
                    )}
                  </CaptionLG>
                </div>

                <Button variant="black" type="button" onClick={handleLoginClick} className="w-full">
                  {t('loginToAccess', 'Log In to Access')}
                </Button>
              </div>
            )}

            {isLoggedIn && (
              <LinkButton
                href={config.watchPath}
                onClick={() => {
                  track('PIF Checkout CTA Clicked', {
                    action: 'Start Watching',
                    offer,
                    projectSlug,
                    isGuest,
                  })
                }}
                variant="black"
                className="w-full"
              >
                {t('startWatching', 'Start Watching')}
              </LinkButton>
            )}

            {perksDisclaimerText && (
              <div className="flex items-start gap-1">
                <CircleInfoIcon size={16} color="core-gray-950" className="shrink-0" />
                <CaptionSM color="core-gray-950" italic className="text-pretty">
                  {perksDisclaimerText}
                </CaptionSM>
              </div>
            )}
          </div>

          <ExpressReceipt projectSlug={projectSlug} offer={offer} />
        </FlexColumn>
        <AppPromoSection />
        <FlexColumn className="relative mx-auto max-w-[640px] gap-8 px-6 pb-16 pt-8">
          <CheckoutLegalTerms className="text-center !text-core-gray-700" />
        </FlexColumn>
      </div>
      <MinimalistFooter className="!pb-12" />
    </ThemeContextProvider>
  )
}
